/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Zorgtraject Versioned Fields API
 * API for managing versioned fields within zorgtrajecten: setting, regiebehandelaar, primair behandelaar (dossierverantwoordelijke), and the GGZ-profiel (the kind of ggz together with the diagnosis that type declares, #3594).  Each field follows a temporal versioning pattern: when the value changes, the current period is closed (einddatum set) and a new period is created (with startdatum). This is called a \"wijziging\" (change).  All operations are scoped to the authenticated user\'s organization. 
 *
 * The version of the OpenAPI document: 1.0.0
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

/**
 * Basis GGZ behandelprofiel
 */

export const BasisGgzProfiel = {
    Kort: 'KORT',
    Middel: 'MIDDEL',
    Intensief: 'INTENSIEF',
    Chronisch: 'CHRONISCH'
} as const;

export type BasisGgzProfiel = typeof BasisGgzProfiel[keyof typeof BasisGgzProfiel];


export interface BehandelaarHistoryResponse {
    'zorgtrajectId': string;
    'current'?: BehandelaarPeriodResponse | null;
    'history': Array<BehandelaarPeriodResponse>;
    'wijzigingCount': number;
}
export interface BehandelaarPeriodResponse {
    'id': string;
    'zorgtrajectId': string;
    'behandelaarId': string;
    /**
     * Full name of the behandelaar
     */
    'behandelaarName': string;
    'startdatum': string;
    'einddatum'?: string | null;
    'reden'?: string | null;
    'isActive': boolean;
    'createdAt': number;
    'createdBy': string;
    'createdByName': string;
}
/**
 * Every property is optional: a correction states only what was wrong. An omitted property keeps its stored value. 
 */
export interface CorrigeerBehandelaarRequest {
    /**
     * Corrected regiebehandelaar. Must be someone the organization designated as regiebehandelaar. Omit to keep the stored one. 
     */
    'behandelaarId'?: string;
    /**
     * Corrected ingangsdatum. May move backwards, including before today; when an earlier period exists it must stay after that period\'s startdatum. Omit to keep the stored date. 
     */
    'startdatum'?: string;
    /**
     * Optional reason for the correction
     */
    'reden'?: string | null;
}
/**
 * Every property is optional: a correction states only what was wrong. An omitted property keeps its stored value.  One exception follows from the type and its diagnosis living on one period (#3594): correcting `ggzType` **drops** the diagnosis belonging to the type being corrected away, unless the request names a replacement for it. A GB-ggz profiel recorded under a type that turns out to be wrong is wrong itself; the audit entry names the drop. 
 */
export interface CorrigeerGgzProfielRequest {
    /**
     * Corrected kind of ggz. Omit to keep the stored one; it may also be left out when a corrected diagnosis implies it. 
     */
    'ggzType'?: GgzType | null;
    /**
     * Corrected GB-ggz profiel (codelijst 033). Refused with a 400 when the corrected type is GESPECIALISEERDE_GGZ. 
     */
    'basisGgzProfiel'?: BasisGgzProfiel | null;
    /**
     * Corrected DSM hoofdgroep (codelijst 029). Refused with a 400 when the corrected type is BASIS_GGZ, and must be valid on the corrected startdatum per CL0005-NZA. 
     */
    'dsmHoofdgroepCode'?: string | null;
    /**
     * Corrected ingangsdatum. May move backwards, including before today; when an earlier period exists it must stay after that period\'s startdatum. Omit to keep the stored date. 
     */
    'startdatum'?: string;
    /**
     * Optional reason for the correction
     */
    'reden'?: string | null;
}


/**
 * Every property is optional: a correction states only what was wrong. An omitted property keeps its stored value. 
 */
export interface CorrigeerPrimairBehandelaarRequest {
    /**
     * Corrected holder. Must be a designated behandelaar of the organization; joins the client\'s behandelteam if not on it. Omit to keep the stored one. 
     */
    'primairBehandelaarId'?: string;
    /**
     * Corrected ingangsdatum. May move backwards, including before today; when an earlier period exists it must stay after that period\'s startdatum. Omit to keep the stored date. 
     */
    'startdatum'?: string;
    /**
     * Optional reason for the correction
     */
    'reden'?: string | null;
}
/**
 * Every property is optional: a correction states only what was wrong. An omitted property keeps its stored value. 
 */
export interface CorrigeerSettingRequest {
    /**
     * Corrected behandelsetting. Omit to keep the stored one.
     */
    'setting'?: Setting | null;
    /**
     * Corrected ingangsdatum. May move backwards, including before today; when an earlier period exists it must stay after that period\'s startdatum. Omit to keep the stored date. 
     */
    'startdatum'?: string;
    /**
     * Optional reason for the correction
     */
    'reden'?: string | null;
}


export interface DsmHoofdgroepCodeInfo {
    /**
     * A DSM hoofdgroep code as published in Vektis codelijst CL0005-NZA — two digits, e.g. `04` (Depressieve stemmingsstoornissen).  Deliberately not an enum: the list is versionable reference data whose entries carry a validity window, so which codes are acceptable depends on the date. The codes live in `dsm_hoofdgroep_codes`, seeded from the committed publication; `GET /api/v1/reference-data/dsm-hoofdgroep-codes` is what a client enumerates. 
     */
    'code': string;
    /**
     * The Betekenis exactly as CL0005-NZA states it
     */
    'betekenis': string;
    /**
     * First day the code is valid; null means open-ended
     */
    'ingangsdatum'?: string | null;
    /**
     * First day the code is no longer valid (Vektis convention — the last valid day is the day before). Null means open-ended. 
     */
    'expiratiedatum'?: string | null;
}
export interface DsmHoofdgroepCodeListResponse {
    /**
     * The date the returned codes were evaluated against
     */
    'peildatum': string;
    /**
     * The codes valid on the peildatum
     */
    'codes': Array<DsmHoofdgroepCodeInfo>;
}
export interface GgzProfielHistoryResponse {
    'zorgtrajectId': string;
    'current'?: GgzProfielPeriodResponse | null;
    'history': Array<GgzProfielPeriodResponse>;
    /**
     * Number of changes made after the initial period
     */
    'wijzigingCount': number;
}
export interface GgzProfielPeriodResponse {
    'id': string;
    'zorgtrajectId': string;
    'ggzType': GgzType;
    /**
     * The GB-ggz profiel; only present on a BASIS_GGZ period
     */
    'basisGgzProfiel'?: BasisGgzProfiel | null;
    /**
     * Human-readable profiel name
     */
    'basisGgzProfielDisplayName'?: string | null;
    /**
     * The DSM hoofdgroep; only present on a GESPECIALISEERDE_GGZ period
     */
    'dsmHoofdgroepCode'?: string | null;
    /**
     * The Betekenis of the hoofdgroep exactly as CL0005-NZA states it
     */
    'dsmHoofdgroepBetekenis'?: string | null;
    'startdatum': string;
    /**
     * End date of this period, derived from the next period\'s startdatum - 1 day. Null for the current period. 
     */
    'einddatum'?: string | null;
    'reden'?: string | null;
    /**
     * Link to the period this one replaced
     */
    'previousId'?: string | null;
    'isActive': boolean;
    'createdAt': number;
    /**
     * User who recorded this period. Null only on periods migrated from the pre-#3594 column state, whose recorder was never stored. 
     */
    'createdBy'?: string | null;
    'createdByName'?: string | null;
}


/**
 * Which kind of ggz a zorgtraject delivers. Chosen per zorgtraject — a practice delivers both — and it decides which diagnosis codelist the declaration carries (Vektis `DiagnoseCodelijstCode`): - BASIS_GGZ: codelijst `033`, the GB-ggz profiel (CL0009-NZA: `GBG01` kort,   `GBG02` middel, `GBG03` intensief, `GBG04` chronisch) - GESPECIALISEERDE_GGZ: codelijst `029`, the DSM hoofdgroep (CL0005-NZA, `01`-`23`)  Absent on a trajectory whose type was never recorded. The codelist is selected from this value alone and never inferred from which diagnosis field happens to be filled, so an untyped trajectory declares no codelist at all. 
 */

export const GgzType = {
    BasisGgz: 'BASIS_GGZ',
    GespecialiseerdeGgz: 'GESPECIALISEERDE_GGZ'
} as const;

export type GgzType = typeof GgzType[keyof typeof GgzType];


export interface PrimairBehandelaarHistoryResponse {
    'zorgtrajectId': string;
    'current'?: PrimairBehandelaarPeriodResponse | null;
    'history': Array<PrimairBehandelaarPeriodResponse>;
    'wijzigingCount': number;
}
export interface PrimairBehandelaarPeriodResponse {
    'id': string;
    'zorgtrajectId': string;
    'primairBehandelaarId': string;
    /**
     * Full name of the primair behandelaar
     */
    'primairBehandelaarName': string;
    'startdatum': string;
    'einddatum'?: string | null;
    'reden'?: string | null;
    'isActive': boolean;
    'createdAt': number;
    'createdBy': string;
    'createdByName': string;
}
/**
 * RFC 7807 Problem Details for HTTP APIs
 */
export interface ProblemDetail {
    /**
     * URI reference identifying the problem type
     */
    'type': string;
    /**
     * Short, human-readable summary
     */
    'title': string;
    /**
     * HTTP status code
     */
    'status': number;
    /**
     * Human-readable explanation
     */
    'detail'?: string;
    /**
     * URI reference identifying the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    'field'?: string;
    'message'?: string;
}
/**
 * Treatment setting (behandelsetting) per NZA tariff structure: - AMBULANT_MONO: Ambulant monodisciplinair (outpatient monodisciplinary) - AMBULANT_MULTI: Ambulant multidisciplinair (outpatient multidisciplinary) - KLINISCH: Klinisch (inpatient clinical) - PRAKTIJK: Praktijk (private practice) 
 */

export const Setting = {
    AmbulantMono: 'AMBULANT_MONO',
    AmbulantMulti: 'AMBULANT_MULTI',
    Klinisch: 'KLINISCH',
    Praktijk: 'PRAKTIJK'
} as const;

export type Setting = typeof Setting[keyof typeof Setting];


export interface SettingHistoryResponse {
    'zorgtrajectId': string;
    'current'?: SettingPeriodResponse | null;
    /**
     * All periods ordered by startdatum descending
     */
    'history': Array<SettingPeriodResponse>;
    /**
     * Number of changes performed
     */
    'wijzigingCount': number;
}
export interface SettingPeriodResponse {
    'id': string;
    'zorgtrajectId': string;
    'setting': Setting;
    /**
     * Human-readable setting name
     */
    'settingDisplayName'?: string;
    'startdatum': string;
    'einddatum'?: string | null;
    /**
     * Reason for change
     */
    'reden'?: string | null;
    'isActive': boolean;
    'createdAt': number;
    'createdBy': string;
    'createdByName': string;
}


export interface WijzigBehandelaarRequest {
    /**
     * User ID of the new regiebehandelaar
     */
    'nieuweBehandelaarId': string;
    /**
     * Start date for the new period (ingangsdatum). Must be > startdatum of current period. Future dates are allowed. 
     */
    'startdatumNieuwe': string;
    'reden'?: string | null;
}
export interface WijzigGgzProfielRequest {
    /**
     * The kind of ggz from the new startdatum. May be omitted when a diagnosis is given: a GB-ggz profiel can only mean basis ggz and a DSM hoofdgroep only gespecialiseerde ggz. A request without type and without diagnosis is refused. 
     */
    'ggzType'?: GgzType | null;
    /**
     * The GB-ggz profiel (codelijst 033). Refused with a 400 when ggzType is GESPECIALISEERDE_GGZ (#3541). 
     */
    'basisGgzProfiel'?: BasisGgzProfiel | null;
    /**
     * The DSM hoofdgroep (codelijst 029). Refused with a 400 when ggzType is BASIS_GGZ, and must be valid on startdatumNieuwe per CL0005-NZA. 
     */
    'dsmHoofdgroepCode'?: string | null;
    /**
     * Start date for the new period (ingangsdatum). Must be > startdatum of current period. Future dates are allowed. 
     */
    'startdatumNieuwe': string;
    'reden'?: string | null;
}


export interface WijzigPrimairBehandelaarRequest {
    /**
     * User ID of the new primair behandelaar (dossierverantwoordelijke). Must be a designated behandelaar of the organization; joins the client\'s behandelteam if not on it. 
     */
    'nieuwePrimairBehandelaarId': string;
    /**
     * Start date for the new period (ingangsdatum). Must be > startdatum of current period. Future dates are allowed. 
     */
    'startdatumNieuwe': string;
    'reden'?: string | null;
}
export interface WijzigSettingRequest {
    'nieuweSetting': Setting;
    /**
     * Start date for the new setting period (ingangsdatum). Must be > startdatum of current period. Future dates are allowed. 
     */
    'startdatumNieuwe': string;
    /**
     * Optional reason for the change
     */
    'reden'?: string | null;
}



/**
 * BehandelaarApi - axios parameter creator
 */
export const BehandelaarApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Corrects the running period in place. This is **not** `wijzigen`: appending a period says the regiebehandelaar changed as of a date, correcting says the entry itself was wrong. Conflating them leaves a mistake permanently in the clinical record (#3597).  Business rules: - The startdatum may move **backwards**, including before today. A value recorded on a   zorgtraject that already exists normally applies from the start of that traject, not   from the day the form happened to be opened. Since #4094 the regiebehandelaar is   resolved per prestatiedatum, so a startdatum a day late names the wrong practitioner   on every consult in the gap - A corrected startdatum that reaches onto or past the start of an earlier period   **replaces** it: that period is removed and this one takes over from the corrected   date. That is how \"it applied from the beginning\" is recorded once a wijziging has   already been appended (#4516). Reaching *into* an earlier period without covering its   start removes nothing — it simply ends the day before this one begins - The corrected startdatum may not fall before the zorgtraject\'s own startdatum (422) - Omitted properties keep their stored value - A corrected behandelaar must be a designated regiebehandelaar (422 otherwise), the   same rule `wijzigen` enforces  Only the current (latest) period is corrected; an earlier period is never edited, it is either left alone or removed whole by a correction that covers its start. Each removal is written to the dossier audit log naming the correction that replaced it (ADR-011). 
         * @summary Correct the current regiebehandelaar period
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {CorrigeerBehandelaarRequest} corrigeerBehandelaarRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        corrigeerBehandelaar: async (zorgtrajectId: string, corrigeerBehandelaarRequest: CorrigeerBehandelaarRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('corrigeerBehandelaar', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'corrigeerBehandelaarRequest' is not null or undefined
            assertParamExists('corrigeerBehandelaar', 'corrigeerBehandelaarRequest', corrigeerBehandelaarRequest)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/behandelaar/huidige`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(corrigeerBehandelaarRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the current regiebehandelaar and complete history for a zorgtraject. History is ordered by startdatum descending (most recent first). 
         * @summary Get behandelaar history for zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBehandelaarHistory: async (zorgtrajectId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('getBehandelaarHistory', 'zorgtrajectId', zorgtrajectId)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/behandelaar`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Removes the running period, which was recorded in error. The preceding period becomes current again; when it was the only one the zorgtraject is left without a regiebehandelaar period, and consults resolve none for their prestatiedatum.  This is the only way to undo an entry: appending a period cannot express \"this should never have been recorded\". The removal is written to the dossier audit log, so the mistake and its correction both stay visible (ADR-011). 
         * @summary Remove the current regiebehandelaar period
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} [reden] Optional reason, recorded on the audit entry
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        verwijderBehandelaar: async (zorgtrajectId: string, reden?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('verwijderBehandelaar', 'zorgtrajectId', zorgtrajectId)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/behandelaar/huidige`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (reden !== undefined) {
                localVarQueryParameter['reden'] = reden;
            }

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Changes the regiebehandelaar of the zorgtraject. Creates a new behandelaar period. The end date of the current period is derived from the new period\'s startdatum - 1 day.  Business rules: - Startdatum of new period must be > startdatum of current period - Future dates are allowed (schedule changes in advance) - New behandelaar must be a valid user in the same organization 
         * @summary Change regiebehandelaar (wijziging)
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {WijzigBehandelaarRequest} wijzigBehandelaarRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wijzigBehandelaar: async (zorgtrajectId: string, wijzigBehandelaarRequest: WijzigBehandelaarRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('wijzigBehandelaar', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'wijzigBehandelaarRequest' is not null or undefined
            assertParamExists('wijzigBehandelaar', 'wijzigBehandelaarRequest', wijzigBehandelaarRequest)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/behandelaar/wijzigen`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(wijzigBehandelaarRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * BehandelaarApi - functional programming interface
 */
export const BehandelaarApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = BehandelaarApiAxiosParamCreator(configuration)
    return {
        /**
         * Corrects the running period in place. This is **not** `wijzigen`: appending a period says the regiebehandelaar changed as of a date, correcting says the entry itself was wrong. Conflating them leaves a mistake permanently in the clinical record (#3597).  Business rules: - The startdatum may move **backwards**, including before today. A value recorded on a   zorgtraject that already exists normally applies from the start of that traject, not   from the day the form happened to be opened. Since #4094 the regiebehandelaar is   resolved per prestatiedatum, so a startdatum a day late names the wrong practitioner   on every consult in the gap - A corrected startdatum that reaches onto or past the start of an earlier period   **replaces** it: that period is removed and this one takes over from the corrected   date. That is how \"it applied from the beginning\" is recorded once a wijziging has   already been appended (#4516). Reaching *into* an earlier period without covering its   start removes nothing — it simply ends the day before this one begins - The corrected startdatum may not fall before the zorgtraject\'s own startdatum (422) - Omitted properties keep their stored value - A corrected behandelaar must be a designated regiebehandelaar (422 otherwise), the   same rule `wijzigen` enforces  Only the current (latest) period is corrected; an earlier period is never edited, it is either left alone or removed whole by a correction that covers its start. Each removal is written to the dossier audit log naming the correction that replaced it (ADR-011). 
         * @summary Correct the current regiebehandelaar period
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {CorrigeerBehandelaarRequest} corrigeerBehandelaarRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async corrigeerBehandelaar(zorgtrajectId: string, corrigeerBehandelaarRequest: CorrigeerBehandelaarRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BehandelaarPeriodResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.corrigeerBehandelaar(zorgtrajectId, corrigeerBehandelaarRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BehandelaarApi.corrigeerBehandelaar']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the current regiebehandelaar and complete history for a zorgtraject. History is ordered by startdatum descending (most recent first). 
         * @summary Get behandelaar history for zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getBehandelaarHistory(zorgtrajectId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BehandelaarHistoryResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getBehandelaarHistory(zorgtrajectId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BehandelaarApi.getBehandelaarHistory']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Removes the running period, which was recorded in error. The preceding period becomes current again; when it was the only one the zorgtraject is left without a regiebehandelaar period, and consults resolve none for their prestatiedatum.  This is the only way to undo an entry: appending a period cannot express \"this should never have been recorded\". The removal is written to the dossier audit log, so the mistake and its correction both stay visible (ADR-011). 
         * @summary Remove the current regiebehandelaar period
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} [reden] Optional reason, recorded on the audit entry
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async verwijderBehandelaar(zorgtrajectId: string, reden?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.verwijderBehandelaar(zorgtrajectId, reden, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BehandelaarApi.verwijderBehandelaar']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Changes the regiebehandelaar of the zorgtraject. Creates a new behandelaar period. The end date of the current period is derived from the new period\'s startdatum - 1 day.  Business rules: - Startdatum of new period must be > startdatum of current period - Future dates are allowed (schedule changes in advance) - New behandelaar must be a valid user in the same organization 
         * @summary Change regiebehandelaar (wijziging)
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {WijzigBehandelaarRequest} wijzigBehandelaarRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wijzigBehandelaar(zorgtrajectId: string, wijzigBehandelaarRequest: WijzigBehandelaarRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BehandelaarPeriodResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.wijzigBehandelaar(zorgtrajectId, wijzigBehandelaarRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BehandelaarApi.wijzigBehandelaar']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * BehandelaarApi - factory interface
 */
export const BehandelaarApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = BehandelaarApiFp(configuration)
    return {
        /**
         * Corrects the running period in place. This is **not** `wijzigen`: appending a period says the regiebehandelaar changed as of a date, correcting says the entry itself was wrong. Conflating them leaves a mistake permanently in the clinical record (#3597).  Business rules: - The startdatum may move **backwards**, including before today. A value recorded on a   zorgtraject that already exists normally applies from the start of that traject, not   from the day the form happened to be opened. Since #4094 the regiebehandelaar is   resolved per prestatiedatum, so a startdatum a day late names the wrong practitioner   on every consult in the gap - A corrected startdatum that reaches onto or past the start of an earlier period   **replaces** it: that period is removed and this one takes over from the corrected   date. That is how \"it applied from the beginning\" is recorded once a wijziging has   already been appended (#4516). Reaching *into* an earlier period without covering its   start removes nothing — it simply ends the day before this one begins - The corrected startdatum may not fall before the zorgtraject\'s own startdatum (422) - Omitted properties keep their stored value - A corrected behandelaar must be a designated regiebehandelaar (422 otherwise), the   same rule `wijzigen` enforces  Only the current (latest) period is corrected; an earlier period is never edited, it is either left alone or removed whole by a correction that covers its start. Each removal is written to the dossier audit log naming the correction that replaced it (ADR-011). 
         * @summary Correct the current regiebehandelaar period
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {CorrigeerBehandelaarRequest} corrigeerBehandelaarRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        corrigeerBehandelaar(zorgtrajectId: string, corrigeerBehandelaarRequest: CorrigeerBehandelaarRequest, options?: RawAxiosRequestConfig): AxiosPromise<BehandelaarPeriodResponse> {
            return localVarFp.corrigeerBehandelaar(zorgtrajectId, corrigeerBehandelaarRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the current regiebehandelaar and complete history for a zorgtraject. History is ordered by startdatum descending (most recent first). 
         * @summary Get behandelaar history for zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBehandelaarHistory(zorgtrajectId: string, options?: RawAxiosRequestConfig): AxiosPromise<BehandelaarHistoryResponse> {
            return localVarFp.getBehandelaarHistory(zorgtrajectId, options).then((request) => request(axios, basePath));
        },
        /**
         * Removes the running period, which was recorded in error. The preceding period becomes current again; when it was the only one the zorgtraject is left without a regiebehandelaar period, and consults resolve none for their prestatiedatum.  This is the only way to undo an entry: appending a period cannot express \"this should never have been recorded\". The removal is written to the dossier audit log, so the mistake and its correction both stay visible (ADR-011). 
         * @summary Remove the current regiebehandelaar period
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} [reden] Optional reason, recorded on the audit entry
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        verwijderBehandelaar(zorgtrajectId: string, reden?: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.verwijderBehandelaar(zorgtrajectId, reden, options).then((request) => request(axios, basePath));
        },
        /**
         * Changes the regiebehandelaar of the zorgtraject. Creates a new behandelaar period. The end date of the current period is derived from the new period\'s startdatum - 1 day.  Business rules: - Startdatum of new period must be > startdatum of current period - Future dates are allowed (schedule changes in advance) - New behandelaar must be a valid user in the same organization 
         * @summary Change regiebehandelaar (wijziging)
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {WijzigBehandelaarRequest} wijzigBehandelaarRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wijzigBehandelaar(zorgtrajectId: string, wijzigBehandelaarRequest: WijzigBehandelaarRequest, options?: RawAxiosRequestConfig): AxiosPromise<BehandelaarPeriodResponse> {
            return localVarFp.wijzigBehandelaar(zorgtrajectId, wijzigBehandelaarRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * BehandelaarApi - object-oriented interface
 */
export class BehandelaarApi extends BaseAPI {
    /**
     * Corrects the running period in place. This is **not** `wijzigen`: appending a period says the regiebehandelaar changed as of a date, correcting says the entry itself was wrong. Conflating them leaves a mistake permanently in the clinical record (#3597).  Business rules: - The startdatum may move **backwards**, including before today. A value recorded on a   zorgtraject that already exists normally applies from the start of that traject, not   from the day the form happened to be opened. Since #4094 the regiebehandelaar is   resolved per prestatiedatum, so a startdatum a day late names the wrong practitioner   on every consult in the gap - A corrected startdatum that reaches onto or past the start of an earlier period   **replaces** it: that period is removed and this one takes over from the corrected   date. That is how \"it applied from the beginning\" is recorded once a wijziging has   already been appended (#4516). Reaching *into* an earlier period without covering its   start removes nothing — it simply ends the day before this one begins - The corrected startdatum may not fall before the zorgtraject\'s own startdatum (422) - Omitted properties keep their stored value - A corrected behandelaar must be a designated regiebehandelaar (422 otherwise), the   same rule `wijzigen` enforces  Only the current (latest) period is corrected; an earlier period is never edited, it is either left alone or removed whole by a correction that covers its start. Each removal is written to the dossier audit log naming the correction that replaced it (ADR-011). 
     * @summary Correct the current regiebehandelaar period
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {CorrigeerBehandelaarRequest} corrigeerBehandelaarRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public corrigeerBehandelaar(zorgtrajectId: string, corrigeerBehandelaarRequest: CorrigeerBehandelaarRequest, options?: RawAxiosRequestConfig) {
        return BehandelaarApiFp(this.configuration).corrigeerBehandelaar(zorgtrajectId, corrigeerBehandelaarRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the current regiebehandelaar and complete history for a zorgtraject. History is ordered by startdatum descending (most recent first). 
     * @summary Get behandelaar history for zorgtraject
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getBehandelaarHistory(zorgtrajectId: string, options?: RawAxiosRequestConfig) {
        return BehandelaarApiFp(this.configuration).getBehandelaarHistory(zorgtrajectId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Removes the running period, which was recorded in error. The preceding period becomes current again; when it was the only one the zorgtraject is left without a regiebehandelaar period, and consults resolve none for their prestatiedatum.  This is the only way to undo an entry: appending a period cannot express \"this should never have been recorded\". The removal is written to the dossier audit log, so the mistake and its correction both stay visible (ADR-011). 
     * @summary Remove the current regiebehandelaar period
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {string} [reden] Optional reason, recorded on the audit entry
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public verwijderBehandelaar(zorgtrajectId: string, reden?: string, options?: RawAxiosRequestConfig) {
        return BehandelaarApiFp(this.configuration).verwijderBehandelaar(zorgtrajectId, reden, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Changes the regiebehandelaar of the zorgtraject. Creates a new behandelaar period. The end date of the current period is derived from the new period\'s startdatum - 1 day.  Business rules: - Startdatum of new period must be > startdatum of current period - Future dates are allowed (schedule changes in advance) - New behandelaar must be a valid user in the same organization 
     * @summary Change regiebehandelaar (wijziging)
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {WijzigBehandelaarRequest} wijzigBehandelaarRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public wijzigBehandelaar(zorgtrajectId: string, wijzigBehandelaarRequest: WijzigBehandelaarRequest, options?: RawAxiosRequestConfig) {
        return BehandelaarApiFp(this.configuration).wijzigBehandelaar(zorgtrajectId, wijzigBehandelaarRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * GgzProfielApi - axios parameter creator
 */
export const GgzProfielApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Corrects the running period in place. This is **not** `wijzigen`: appending a period says the type or the diagnosis changed as of a date, correcting says the entry itself was wrong. Conflating them leaves a mistake permanently in the clinical record (#3597).  One endpoint corrects the whole period, because one period carries the type together with its diagnosis (#3594) — the GB-ggz profiel for basis ggz, the DSM hoofdgroep for gespecialiseerde ggz. Correcting them separately is what let the two drift apart.  Business rules: - The startdatum may move **backwards**, including before today. A value recorded on a   zorgtraject that already exists normally applies from the start of that traject, not   from the day the form happened to be opened. Since billing resolves the profiel per   prestatiedatum, a startdatum a day late silently makes every session before it   undeclarable — which is how this was reported - A corrected startdatum that reaches onto or past the start of an earlier period   **replaces** it: that period is removed and this one takes over from the corrected   date. That is how \"it applied from the beginning\" is recorded once a wijziging has   already been appended (#4516). Reaching *into* an earlier period without covering its   start removes nothing — it simply ends the day before this one begins - The corrected startdatum may not fall before the zorgtraject\'s own startdatum (422) - Omitted properties keep their stored value, except that correcting `ggzType` drops   the diagnosis belonging to the type corrected away when no replacement is named - A request stating a diagnosis that contradicts the type it states is refused with a   `400`: `basisGgzProfiel` only with `BASIS_GGZ`, `dsmHoofdgroepCode` only with   `GESPECIALISEERDE_GGZ` - `dsmHoofdgroepCode` must be valid on the corrected startdatum per CL0005-NZA  Only the current (latest) period is corrected; an earlier period is never edited, it is either left alone or removed whole by a correction that covers its start. Each removal is written to the dossier audit log naming the correction that replaced it (ADR-011). 
         * @summary Correct the current GGZ-profiel period
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {CorrigeerGgzProfielRequest} corrigeerGgzProfielRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        corrigeerGgzProfiel: async (zorgtrajectId: string, corrigeerGgzProfielRequest: CorrigeerGgzProfielRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('corrigeerGgzProfiel', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'corrigeerGgzProfielRequest' is not null or undefined
            assertParamExists('corrigeerGgzProfiel', 'corrigeerGgzProfielRequest', corrigeerGgzProfielRequest)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/ggz-profiel/huidige`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(corrigeerGgzProfielRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the current GGZ-profiel period and the complete history for a zorgtraject. One period carries the kind of ggz (basis or gespecialiseerd) together with the diagnosis that type declares: the GB-ggz profiel (codelijst `033`) for basis ggz, the DSM hoofdgroep (codelijst `029`) for gespecialiseerde ggz. History is ordered by startdatum descending (most recent first). 
         * @summary Get the GGZ-profiel history for a zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getGgzProfielHistory: async (zorgtrajectId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('getGgzProfielHistory', 'zorgtrajectId', zorgtrajectId)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/ggz-profiel`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Removes the running period, which was recorded in error. The preceding period becomes current again; when it was the only one the zorgtraject is left without a GGZ-profiel — the intake state, which `billing.missing-dsm-hoofdgroep` signals.  This is the only way to undo an entry: appending a period cannot express \"this should never have been recorded\". The removal is written to the dossier audit log, so the mistake and its correction both stay visible (ADR-011). 
         * @summary Remove the current GGZ-profiel period
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} [reden] Optional reason, recorded on the audit entry
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        verwijderGgzProfiel: async (zorgtrajectId: string, reden?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('verwijderGgzProfiel', 'zorgtrajectId', zorgtrajectId)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/ggz-profiel/huidige`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (reden !== undefined) {
                localVarQueryParameter['reden'] = reden;
            }

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Appends a period stating the kind of ggz and its diagnosis from the given startdatum. The previous period\'s einddatum is derived from the new period\'s startdatum - 1 day; nothing is overwritten, so the type and diagnosis in force on any earlier date stay retrievable.  First-time mode: when the zorgtraject has no GGZ-profiel period yet, this endpoint records the initial one instead of a change.  Business rules: - Startdatum of the new period must be > startdatum of the current period - Future dates are allowed (schedule a change in advance) - The diagnosis must belong to the type: `basisGgzProfiel` only with   `BASIS_GGZ`, `dsmHoofdgroepCode` only with `GESPECIALISEERDE_GGZ`.   A contradiction returns a `400` with field-level Problem Details (#3541) - `ggzType` may be omitted when a diagnosis is given — a GB-ggz profiel   can only mean basis ggz and a DSM hoofdgroep only gespecialiseerde ggz,   so the type follows from the diagnosis - A period without any diagnosis is allowed (intake state); a request   without type and without diagnosis is refused - `dsmHoofdgroepCode` must be valid on the startdatum per CL0005-NZA; an   expired code (`08`, `20`) is refused 
         * @summary Record a new GGZ-profiel period, or the first one
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {WijzigGgzProfielRequest} wijzigGgzProfielRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wijzigGgzProfiel: async (zorgtrajectId: string, wijzigGgzProfielRequest: WijzigGgzProfielRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('wijzigGgzProfiel', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'wijzigGgzProfielRequest' is not null or undefined
            assertParamExists('wijzigGgzProfiel', 'wijzigGgzProfielRequest', wijzigGgzProfielRequest)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/ggz-profiel/wijzigen`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(wijzigGgzProfielRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * GgzProfielApi - functional programming interface
 */
export const GgzProfielApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = GgzProfielApiAxiosParamCreator(configuration)
    return {
        /**
         * Corrects the running period in place. This is **not** `wijzigen`: appending a period says the type or the diagnosis changed as of a date, correcting says the entry itself was wrong. Conflating them leaves a mistake permanently in the clinical record (#3597).  One endpoint corrects the whole period, because one period carries the type together with its diagnosis (#3594) — the GB-ggz profiel for basis ggz, the DSM hoofdgroep for gespecialiseerde ggz. Correcting them separately is what let the two drift apart.  Business rules: - The startdatum may move **backwards**, including before today. A value recorded on a   zorgtraject that already exists normally applies from the start of that traject, not   from the day the form happened to be opened. Since billing resolves the profiel per   prestatiedatum, a startdatum a day late silently makes every session before it   undeclarable — which is how this was reported - A corrected startdatum that reaches onto or past the start of an earlier period   **replaces** it: that period is removed and this one takes over from the corrected   date. That is how \"it applied from the beginning\" is recorded once a wijziging has   already been appended (#4516). Reaching *into* an earlier period without covering its   start removes nothing — it simply ends the day before this one begins - The corrected startdatum may not fall before the zorgtraject\'s own startdatum (422) - Omitted properties keep their stored value, except that correcting `ggzType` drops   the diagnosis belonging to the type corrected away when no replacement is named - A request stating a diagnosis that contradicts the type it states is refused with a   `400`: `basisGgzProfiel` only with `BASIS_GGZ`, `dsmHoofdgroepCode` only with   `GESPECIALISEERDE_GGZ` - `dsmHoofdgroepCode` must be valid on the corrected startdatum per CL0005-NZA  Only the current (latest) period is corrected; an earlier period is never edited, it is either left alone or removed whole by a correction that covers its start. Each removal is written to the dossier audit log naming the correction that replaced it (ADR-011). 
         * @summary Correct the current GGZ-profiel period
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {CorrigeerGgzProfielRequest} corrigeerGgzProfielRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async corrigeerGgzProfiel(zorgtrajectId: string, corrigeerGgzProfielRequest: CorrigeerGgzProfielRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GgzProfielPeriodResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.corrigeerGgzProfiel(zorgtrajectId, corrigeerGgzProfielRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['GgzProfielApi.corrigeerGgzProfiel']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the current GGZ-profiel period and the complete history for a zorgtraject. One period carries the kind of ggz (basis or gespecialiseerd) together with the diagnosis that type declares: the GB-ggz profiel (codelijst `033`) for basis ggz, the DSM hoofdgroep (codelijst `029`) for gespecialiseerde ggz. History is ordered by startdatum descending (most recent first). 
         * @summary Get the GGZ-profiel history for a zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getGgzProfielHistory(zorgtrajectId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GgzProfielHistoryResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getGgzProfielHistory(zorgtrajectId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['GgzProfielApi.getGgzProfielHistory']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Removes the running period, which was recorded in error. The preceding period becomes current again; when it was the only one the zorgtraject is left without a GGZ-profiel — the intake state, which `billing.missing-dsm-hoofdgroep` signals.  This is the only way to undo an entry: appending a period cannot express \"this should never have been recorded\". The removal is written to the dossier audit log, so the mistake and its correction both stay visible (ADR-011). 
         * @summary Remove the current GGZ-profiel period
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} [reden] Optional reason, recorded on the audit entry
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async verwijderGgzProfiel(zorgtrajectId: string, reden?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.verwijderGgzProfiel(zorgtrajectId, reden, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['GgzProfielApi.verwijderGgzProfiel']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Appends a period stating the kind of ggz and its diagnosis from the given startdatum. The previous period\'s einddatum is derived from the new period\'s startdatum - 1 day; nothing is overwritten, so the type and diagnosis in force on any earlier date stay retrievable.  First-time mode: when the zorgtraject has no GGZ-profiel period yet, this endpoint records the initial one instead of a change.  Business rules: - Startdatum of the new period must be > startdatum of the current period - Future dates are allowed (schedule a change in advance) - The diagnosis must belong to the type: `basisGgzProfiel` only with   `BASIS_GGZ`, `dsmHoofdgroepCode` only with `GESPECIALISEERDE_GGZ`.   A contradiction returns a `400` with field-level Problem Details (#3541) - `ggzType` may be omitted when a diagnosis is given — a GB-ggz profiel   can only mean basis ggz and a DSM hoofdgroep only gespecialiseerde ggz,   so the type follows from the diagnosis - A period without any diagnosis is allowed (intake state); a request   without type and without diagnosis is refused - `dsmHoofdgroepCode` must be valid on the startdatum per CL0005-NZA; an   expired code (`08`, `20`) is refused 
         * @summary Record a new GGZ-profiel period, or the first one
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {WijzigGgzProfielRequest} wijzigGgzProfielRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wijzigGgzProfiel(zorgtrajectId: string, wijzigGgzProfielRequest: WijzigGgzProfielRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GgzProfielPeriodResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.wijzigGgzProfiel(zorgtrajectId, wijzigGgzProfielRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['GgzProfielApi.wijzigGgzProfiel']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * GgzProfielApi - factory interface
 */
export const GgzProfielApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = GgzProfielApiFp(configuration)
    return {
        /**
         * Corrects the running period in place. This is **not** `wijzigen`: appending a period says the type or the diagnosis changed as of a date, correcting says the entry itself was wrong. Conflating them leaves a mistake permanently in the clinical record (#3597).  One endpoint corrects the whole period, because one period carries the type together with its diagnosis (#3594) — the GB-ggz profiel for basis ggz, the DSM hoofdgroep for gespecialiseerde ggz. Correcting them separately is what let the two drift apart.  Business rules: - The startdatum may move **backwards**, including before today. A value recorded on a   zorgtraject that already exists normally applies from the start of that traject, not   from the day the form happened to be opened. Since billing resolves the profiel per   prestatiedatum, a startdatum a day late silently makes every session before it   undeclarable — which is how this was reported - A corrected startdatum that reaches onto or past the start of an earlier period   **replaces** it: that period is removed and this one takes over from the corrected   date. That is how \"it applied from the beginning\" is recorded once a wijziging has   already been appended (#4516). Reaching *into* an earlier period without covering its   start removes nothing — it simply ends the day before this one begins - The corrected startdatum may not fall before the zorgtraject\'s own startdatum (422) - Omitted properties keep their stored value, except that correcting `ggzType` drops   the diagnosis belonging to the type corrected away when no replacement is named - A request stating a diagnosis that contradicts the type it states is refused with a   `400`: `basisGgzProfiel` only with `BASIS_GGZ`, `dsmHoofdgroepCode` only with   `GESPECIALISEERDE_GGZ` - `dsmHoofdgroepCode` must be valid on the corrected startdatum per CL0005-NZA  Only the current (latest) period is corrected; an earlier period is never edited, it is either left alone or removed whole by a correction that covers its start. Each removal is written to the dossier audit log naming the correction that replaced it (ADR-011). 
         * @summary Correct the current GGZ-profiel period
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {CorrigeerGgzProfielRequest} corrigeerGgzProfielRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        corrigeerGgzProfiel(zorgtrajectId: string, corrigeerGgzProfielRequest: CorrigeerGgzProfielRequest, options?: RawAxiosRequestConfig): AxiosPromise<GgzProfielPeriodResponse> {
            return localVarFp.corrigeerGgzProfiel(zorgtrajectId, corrigeerGgzProfielRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the current GGZ-profiel period and the complete history for a zorgtraject. One period carries the kind of ggz (basis or gespecialiseerd) together with the diagnosis that type declares: the GB-ggz profiel (codelijst `033`) for basis ggz, the DSM hoofdgroep (codelijst `029`) for gespecialiseerde ggz. History is ordered by startdatum descending (most recent first). 
         * @summary Get the GGZ-profiel history for a zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getGgzProfielHistory(zorgtrajectId: string, options?: RawAxiosRequestConfig): AxiosPromise<GgzProfielHistoryResponse> {
            return localVarFp.getGgzProfielHistory(zorgtrajectId, options).then((request) => request(axios, basePath));
        },
        /**
         * Removes the running period, which was recorded in error. The preceding period becomes current again; when it was the only one the zorgtraject is left without a GGZ-profiel — the intake state, which `billing.missing-dsm-hoofdgroep` signals.  This is the only way to undo an entry: appending a period cannot express \"this should never have been recorded\". The removal is written to the dossier audit log, so the mistake and its correction both stay visible (ADR-011). 
         * @summary Remove the current GGZ-profiel period
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} [reden] Optional reason, recorded on the audit entry
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        verwijderGgzProfiel(zorgtrajectId: string, reden?: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.verwijderGgzProfiel(zorgtrajectId, reden, options).then((request) => request(axios, basePath));
        },
        /**
         * Appends a period stating the kind of ggz and its diagnosis from the given startdatum. The previous period\'s einddatum is derived from the new period\'s startdatum - 1 day; nothing is overwritten, so the type and diagnosis in force on any earlier date stay retrievable.  First-time mode: when the zorgtraject has no GGZ-profiel period yet, this endpoint records the initial one instead of a change.  Business rules: - Startdatum of the new period must be > startdatum of the current period - Future dates are allowed (schedule a change in advance) - The diagnosis must belong to the type: `basisGgzProfiel` only with   `BASIS_GGZ`, `dsmHoofdgroepCode` only with `GESPECIALISEERDE_GGZ`.   A contradiction returns a `400` with field-level Problem Details (#3541) - `ggzType` may be omitted when a diagnosis is given — a GB-ggz profiel   can only mean basis ggz and a DSM hoofdgroep only gespecialiseerde ggz,   so the type follows from the diagnosis - A period without any diagnosis is allowed (intake state); a request   without type and without diagnosis is refused - `dsmHoofdgroepCode` must be valid on the startdatum per CL0005-NZA; an   expired code (`08`, `20`) is refused 
         * @summary Record a new GGZ-profiel period, or the first one
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {WijzigGgzProfielRequest} wijzigGgzProfielRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wijzigGgzProfiel(zorgtrajectId: string, wijzigGgzProfielRequest: WijzigGgzProfielRequest, options?: RawAxiosRequestConfig): AxiosPromise<GgzProfielPeriodResponse> {
            return localVarFp.wijzigGgzProfiel(zorgtrajectId, wijzigGgzProfielRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * GgzProfielApi - object-oriented interface
 */
export class GgzProfielApi extends BaseAPI {
    /**
     * Corrects the running period in place. This is **not** `wijzigen`: appending a period says the type or the diagnosis changed as of a date, correcting says the entry itself was wrong. Conflating them leaves a mistake permanently in the clinical record (#3597).  One endpoint corrects the whole period, because one period carries the type together with its diagnosis (#3594) — the GB-ggz profiel for basis ggz, the DSM hoofdgroep for gespecialiseerde ggz. Correcting them separately is what let the two drift apart.  Business rules: - The startdatum may move **backwards**, including before today. A value recorded on a   zorgtraject that already exists normally applies from the start of that traject, not   from the day the form happened to be opened. Since billing resolves the profiel per   prestatiedatum, a startdatum a day late silently makes every session before it   undeclarable — which is how this was reported - A corrected startdatum that reaches onto or past the start of an earlier period   **replaces** it: that period is removed and this one takes over from the corrected   date. That is how \"it applied from the beginning\" is recorded once a wijziging has   already been appended (#4516). Reaching *into* an earlier period without covering its   start removes nothing — it simply ends the day before this one begins - The corrected startdatum may not fall before the zorgtraject\'s own startdatum (422) - Omitted properties keep their stored value, except that correcting `ggzType` drops   the diagnosis belonging to the type corrected away when no replacement is named - A request stating a diagnosis that contradicts the type it states is refused with a   `400`: `basisGgzProfiel` only with `BASIS_GGZ`, `dsmHoofdgroepCode` only with   `GESPECIALISEERDE_GGZ` - `dsmHoofdgroepCode` must be valid on the corrected startdatum per CL0005-NZA  Only the current (latest) period is corrected; an earlier period is never edited, it is either left alone or removed whole by a correction that covers its start. Each removal is written to the dossier audit log naming the correction that replaced it (ADR-011). 
     * @summary Correct the current GGZ-profiel period
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {CorrigeerGgzProfielRequest} corrigeerGgzProfielRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public corrigeerGgzProfiel(zorgtrajectId: string, corrigeerGgzProfielRequest: CorrigeerGgzProfielRequest, options?: RawAxiosRequestConfig) {
        return GgzProfielApiFp(this.configuration).corrigeerGgzProfiel(zorgtrajectId, corrigeerGgzProfielRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the current GGZ-profiel period and the complete history for a zorgtraject. One period carries the kind of ggz (basis or gespecialiseerd) together with the diagnosis that type declares: the GB-ggz profiel (codelijst `033`) for basis ggz, the DSM hoofdgroep (codelijst `029`) for gespecialiseerde ggz. History is ordered by startdatum descending (most recent first). 
     * @summary Get the GGZ-profiel history for a zorgtraject
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getGgzProfielHistory(zorgtrajectId: string, options?: RawAxiosRequestConfig) {
        return GgzProfielApiFp(this.configuration).getGgzProfielHistory(zorgtrajectId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Removes the running period, which was recorded in error. The preceding period becomes current again; when it was the only one the zorgtraject is left without a GGZ-profiel — the intake state, which `billing.missing-dsm-hoofdgroep` signals.  This is the only way to undo an entry: appending a period cannot express \"this should never have been recorded\". The removal is written to the dossier audit log, so the mistake and its correction both stay visible (ADR-011). 
     * @summary Remove the current GGZ-profiel period
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {string} [reden] Optional reason, recorded on the audit entry
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public verwijderGgzProfiel(zorgtrajectId: string, reden?: string, options?: RawAxiosRequestConfig) {
        return GgzProfielApiFp(this.configuration).verwijderGgzProfiel(zorgtrajectId, reden, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Appends a period stating the kind of ggz and its diagnosis from the given startdatum. The previous period\'s einddatum is derived from the new period\'s startdatum - 1 day; nothing is overwritten, so the type and diagnosis in force on any earlier date stay retrievable.  First-time mode: when the zorgtraject has no GGZ-profiel period yet, this endpoint records the initial one instead of a change.  Business rules: - Startdatum of the new period must be > startdatum of the current period - Future dates are allowed (schedule a change in advance) - The diagnosis must belong to the type: `basisGgzProfiel` only with   `BASIS_GGZ`, `dsmHoofdgroepCode` only with `GESPECIALISEERDE_GGZ`.   A contradiction returns a `400` with field-level Problem Details (#3541) - `ggzType` may be omitted when a diagnosis is given — a GB-ggz profiel   can only mean basis ggz and a DSM hoofdgroep only gespecialiseerde ggz,   so the type follows from the diagnosis - A period without any diagnosis is allowed (intake state); a request   without type and without diagnosis is refused - `dsmHoofdgroepCode` must be valid on the startdatum per CL0005-NZA; an   expired code (`08`, `20`) is refused 
     * @summary Record a new GGZ-profiel period, or the first one
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {WijzigGgzProfielRequest} wijzigGgzProfielRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public wijzigGgzProfiel(zorgtrajectId: string, wijzigGgzProfielRequest: WijzigGgzProfielRequest, options?: RawAxiosRequestConfig) {
        return GgzProfielApiFp(this.configuration).wijzigGgzProfiel(zorgtrajectId, wijzigGgzProfielRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * PrimairBehandelaarApi - axios parameter creator
 */
export const PrimairBehandelaarApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Corrects the running period in place. This is **not** `wijzigen`: appending a period says the dossierverantwoordelijke changed as of a date, correcting says the entry itself was wrong. Conflating them leaves a mistake permanently in the clinical record (#3597).  Business rules: - The startdatum may move **backwards**, including before today. A value recorded on a   zorgtraject that already exists normally applies from the start of that traject, not   from the day the form happened to be opened - A corrected startdatum that reaches onto or past the start of an earlier period   **replaces** it: that period is removed and this one takes over from the corrected   date. That is how \"it applied from the beginning\" is recorded once a wijziging has   already been appended (#4516). Reaching *into* an earlier period without covering its   start removes nothing — it simply ends the day before this one begins - The corrected startdatum may not fall before the zorgtraject\'s own startdatum (422) - Omitted properties keep their stored value - A changed holder follows the same rules as `wijzigen`: a designated behandelaar   (422 otherwise), added to the behandelteam if not on it, which requires   `clients.assign` (403 otherwise)  Only the current (latest) period is corrected; an earlier period is never edited, it is either left alone or removed whole by a correction that covers its start. Each removal is written to the dossier audit log naming the correction that replaced it (ADR-011). 
         * @summary Correct the current dossierverantwoordelijke period
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {CorrigeerPrimairBehandelaarRequest} corrigeerPrimairBehandelaarRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        corrigeerPrimairBehandelaar: async (zorgtrajectId: string, corrigeerPrimairBehandelaarRequest: CorrigeerPrimairBehandelaarRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('corrigeerPrimairBehandelaar', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'corrigeerPrimairBehandelaarRequest' is not null or undefined
            assertParamExists('corrigeerPrimairBehandelaar', 'corrigeerPrimairBehandelaarRequest', corrigeerPrimairBehandelaarRequest)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/primair-behandelaar/huidige`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(corrigeerPrimairBehandelaarRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the current primair behandelaar (dossierverantwoordelijke) and complete history for a zorgtraject. History is ordered by startdatum descending (most recent first).  A zorgtraject created without a primair behandelaar has an empty history and a null `current`. 
         * @summary Get primair behandelaar history for zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getPrimairBehandelaarHistory: async (zorgtrajectId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('getPrimairBehandelaarHistory', 'zorgtrajectId', zorgtrajectId)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/primair-behandelaar`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Removes the running period, which was recorded in error. The preceding period becomes current again; when it was the only one the zorgtraject is left without a dossierverantwoordelijke.  This is the only way to undo an entry: appending a period cannot express \"this should never have been recorded\". The removal is written to the dossier audit log, so the mistake and its correction both stay visible (ADR-011). 
         * @summary Remove the current dossierverantwoordelijke period
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} [reden] Optional reason, recorded on the audit entry
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        verwijderPrimairBehandelaar: async (zorgtrajectId: string, reden?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('verwijderPrimairBehandelaar', 'zorgtrajectId', zorgtrajectId)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/primair-behandelaar/huidige`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (reden !== undefined) {
                localVarQueryParameter['reden'] = reden;
            }

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Changes the primair behandelaar (dossierverantwoordelijke) of the zorgtraject. Creates a new period; the end date of the current period is derived from the new period\'s startdatum - 1 day.  Business rules: - Startdatum of new period must be > startdatum of current period - Future dates are allowed (schedule changes in advance) - The new primair behandelaar must be an enabled, designated behandelaar of the   organization (422 otherwise), the same pool the create path offers - Someone not yet on the client\'s behandelteam is added to it, which requires   `clients.assign` (403 otherwise); choosing a current member needs nothing more 
         * @summary Change primair behandelaar (wijziging)
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {WijzigPrimairBehandelaarRequest} wijzigPrimairBehandelaarRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wijzigPrimairBehandelaar: async (zorgtrajectId: string, wijzigPrimairBehandelaarRequest: WijzigPrimairBehandelaarRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('wijzigPrimairBehandelaar', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'wijzigPrimairBehandelaarRequest' is not null or undefined
            assertParamExists('wijzigPrimairBehandelaar', 'wijzigPrimairBehandelaarRequest', wijzigPrimairBehandelaarRequest)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/primair-behandelaar/wijzigen`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(wijzigPrimairBehandelaarRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * PrimairBehandelaarApi - functional programming interface
 */
export const PrimairBehandelaarApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = PrimairBehandelaarApiAxiosParamCreator(configuration)
    return {
        /**
         * Corrects the running period in place. This is **not** `wijzigen`: appending a period says the dossierverantwoordelijke changed as of a date, correcting says the entry itself was wrong. Conflating them leaves a mistake permanently in the clinical record (#3597).  Business rules: - The startdatum may move **backwards**, including before today. A value recorded on a   zorgtraject that already exists normally applies from the start of that traject, not   from the day the form happened to be opened - A corrected startdatum that reaches onto or past the start of an earlier period   **replaces** it: that period is removed and this one takes over from the corrected   date. That is how \"it applied from the beginning\" is recorded once a wijziging has   already been appended (#4516). Reaching *into* an earlier period without covering its   start removes nothing — it simply ends the day before this one begins - The corrected startdatum may not fall before the zorgtraject\'s own startdatum (422) - Omitted properties keep their stored value - A changed holder follows the same rules as `wijzigen`: a designated behandelaar   (422 otherwise), added to the behandelteam if not on it, which requires   `clients.assign` (403 otherwise)  Only the current (latest) period is corrected; an earlier period is never edited, it is either left alone or removed whole by a correction that covers its start. Each removal is written to the dossier audit log naming the correction that replaced it (ADR-011). 
         * @summary Correct the current dossierverantwoordelijke period
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {CorrigeerPrimairBehandelaarRequest} corrigeerPrimairBehandelaarRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async corrigeerPrimairBehandelaar(zorgtrajectId: string, corrigeerPrimairBehandelaarRequest: CorrigeerPrimairBehandelaarRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PrimairBehandelaarPeriodResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.corrigeerPrimairBehandelaar(zorgtrajectId, corrigeerPrimairBehandelaarRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['PrimairBehandelaarApi.corrigeerPrimairBehandelaar']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the current primair behandelaar (dossierverantwoordelijke) and complete history for a zorgtraject. History is ordered by startdatum descending (most recent first).  A zorgtraject created without a primair behandelaar has an empty history and a null `current`. 
         * @summary Get primair behandelaar history for zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getPrimairBehandelaarHistory(zorgtrajectId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PrimairBehandelaarHistoryResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getPrimairBehandelaarHistory(zorgtrajectId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['PrimairBehandelaarApi.getPrimairBehandelaarHistory']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Removes the running period, which was recorded in error. The preceding period becomes current again; when it was the only one the zorgtraject is left without a dossierverantwoordelijke.  This is the only way to undo an entry: appending a period cannot express \"this should never have been recorded\". The removal is written to the dossier audit log, so the mistake and its correction both stay visible (ADR-011). 
         * @summary Remove the current dossierverantwoordelijke period
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} [reden] Optional reason, recorded on the audit entry
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async verwijderPrimairBehandelaar(zorgtrajectId: string, reden?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.verwijderPrimairBehandelaar(zorgtrajectId, reden, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['PrimairBehandelaarApi.verwijderPrimairBehandelaar']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Changes the primair behandelaar (dossierverantwoordelijke) of the zorgtraject. Creates a new period; the end date of the current period is derived from the new period\'s startdatum - 1 day.  Business rules: - Startdatum of new period must be > startdatum of current period - Future dates are allowed (schedule changes in advance) - The new primair behandelaar must be an enabled, designated behandelaar of the   organization (422 otherwise), the same pool the create path offers - Someone not yet on the client\'s behandelteam is added to it, which requires   `clients.assign` (403 otherwise); choosing a current member needs nothing more 
         * @summary Change primair behandelaar (wijziging)
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {WijzigPrimairBehandelaarRequest} wijzigPrimairBehandelaarRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wijzigPrimairBehandelaar(zorgtrajectId: string, wijzigPrimairBehandelaarRequest: WijzigPrimairBehandelaarRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PrimairBehandelaarPeriodResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.wijzigPrimairBehandelaar(zorgtrajectId, wijzigPrimairBehandelaarRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['PrimairBehandelaarApi.wijzigPrimairBehandelaar']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * PrimairBehandelaarApi - factory interface
 */
export const PrimairBehandelaarApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = PrimairBehandelaarApiFp(configuration)
    return {
        /**
         * Corrects the running period in place. This is **not** `wijzigen`: appending a period says the dossierverantwoordelijke changed as of a date, correcting says the entry itself was wrong. Conflating them leaves a mistake permanently in the clinical record (#3597).  Business rules: - The startdatum may move **backwards**, including before today. A value recorded on a   zorgtraject that already exists normally applies from the start of that traject, not   from the day the form happened to be opened - A corrected startdatum that reaches onto or past the start of an earlier period   **replaces** it: that period is removed and this one takes over from the corrected   date. That is how \"it applied from the beginning\" is recorded once a wijziging has   already been appended (#4516). Reaching *into* an earlier period without covering its   start removes nothing — it simply ends the day before this one begins - The corrected startdatum may not fall before the zorgtraject\'s own startdatum (422) - Omitted properties keep their stored value - A changed holder follows the same rules as `wijzigen`: a designated behandelaar   (422 otherwise), added to the behandelteam if not on it, which requires   `clients.assign` (403 otherwise)  Only the current (latest) period is corrected; an earlier period is never edited, it is either left alone or removed whole by a correction that covers its start. Each removal is written to the dossier audit log naming the correction that replaced it (ADR-011). 
         * @summary Correct the current dossierverantwoordelijke period
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {CorrigeerPrimairBehandelaarRequest} corrigeerPrimairBehandelaarRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        corrigeerPrimairBehandelaar(zorgtrajectId: string, corrigeerPrimairBehandelaarRequest: CorrigeerPrimairBehandelaarRequest, options?: RawAxiosRequestConfig): AxiosPromise<PrimairBehandelaarPeriodResponse> {
            return localVarFp.corrigeerPrimairBehandelaar(zorgtrajectId, corrigeerPrimairBehandelaarRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the current primair behandelaar (dossierverantwoordelijke) and complete history for a zorgtraject. History is ordered by startdatum descending (most recent first).  A zorgtraject created without a primair behandelaar has an empty history and a null `current`. 
         * @summary Get primair behandelaar history for zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getPrimairBehandelaarHistory(zorgtrajectId: string, options?: RawAxiosRequestConfig): AxiosPromise<PrimairBehandelaarHistoryResponse> {
            return localVarFp.getPrimairBehandelaarHistory(zorgtrajectId, options).then((request) => request(axios, basePath));
        },
        /**
         * Removes the running period, which was recorded in error. The preceding period becomes current again; when it was the only one the zorgtraject is left without a dossierverantwoordelijke.  This is the only way to undo an entry: appending a period cannot express \"this should never have been recorded\". The removal is written to the dossier audit log, so the mistake and its correction both stay visible (ADR-011). 
         * @summary Remove the current dossierverantwoordelijke period
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} [reden] Optional reason, recorded on the audit entry
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        verwijderPrimairBehandelaar(zorgtrajectId: string, reden?: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.verwijderPrimairBehandelaar(zorgtrajectId, reden, options).then((request) => request(axios, basePath));
        },
        /**
         * Changes the primair behandelaar (dossierverantwoordelijke) of the zorgtraject. Creates a new period; the end date of the current period is derived from the new period\'s startdatum - 1 day.  Business rules: - Startdatum of new period must be > startdatum of current period - Future dates are allowed (schedule changes in advance) - The new primair behandelaar must be an enabled, designated behandelaar of the   organization (422 otherwise), the same pool the create path offers - Someone not yet on the client\'s behandelteam is added to it, which requires   `clients.assign` (403 otherwise); choosing a current member needs nothing more 
         * @summary Change primair behandelaar (wijziging)
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {WijzigPrimairBehandelaarRequest} wijzigPrimairBehandelaarRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wijzigPrimairBehandelaar(zorgtrajectId: string, wijzigPrimairBehandelaarRequest: WijzigPrimairBehandelaarRequest, options?: RawAxiosRequestConfig): AxiosPromise<PrimairBehandelaarPeriodResponse> {
            return localVarFp.wijzigPrimairBehandelaar(zorgtrajectId, wijzigPrimairBehandelaarRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * PrimairBehandelaarApi - object-oriented interface
 */
export class PrimairBehandelaarApi extends BaseAPI {
    /**
     * Corrects the running period in place. This is **not** `wijzigen`: appending a period says the dossierverantwoordelijke changed as of a date, correcting says the entry itself was wrong. Conflating them leaves a mistake permanently in the clinical record (#3597).  Business rules: - The startdatum may move **backwards**, including before today. A value recorded on a   zorgtraject that already exists normally applies from the start of that traject, not   from the day the form happened to be opened - A corrected startdatum that reaches onto or past the start of an earlier period   **replaces** it: that period is removed and this one takes over from the corrected   date. That is how \"it applied from the beginning\" is recorded once a wijziging has   already been appended (#4516). Reaching *into* an earlier period without covering its   start removes nothing — it simply ends the day before this one begins - The corrected startdatum may not fall before the zorgtraject\'s own startdatum (422) - Omitted properties keep their stored value - A changed holder follows the same rules as `wijzigen`: a designated behandelaar   (422 otherwise), added to the behandelteam if not on it, which requires   `clients.assign` (403 otherwise)  Only the current (latest) period is corrected; an earlier period is never edited, it is either left alone or removed whole by a correction that covers its start. Each removal is written to the dossier audit log naming the correction that replaced it (ADR-011). 
     * @summary Correct the current dossierverantwoordelijke period
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {CorrigeerPrimairBehandelaarRequest} corrigeerPrimairBehandelaarRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public corrigeerPrimairBehandelaar(zorgtrajectId: string, corrigeerPrimairBehandelaarRequest: CorrigeerPrimairBehandelaarRequest, options?: RawAxiosRequestConfig) {
        return PrimairBehandelaarApiFp(this.configuration).corrigeerPrimairBehandelaar(zorgtrajectId, corrigeerPrimairBehandelaarRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the current primair behandelaar (dossierverantwoordelijke) and complete history for a zorgtraject. History is ordered by startdatum descending (most recent first).  A zorgtraject created without a primair behandelaar has an empty history and a null `current`. 
     * @summary Get primair behandelaar history for zorgtraject
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getPrimairBehandelaarHistory(zorgtrajectId: string, options?: RawAxiosRequestConfig) {
        return PrimairBehandelaarApiFp(this.configuration).getPrimairBehandelaarHistory(zorgtrajectId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Removes the running period, which was recorded in error. The preceding period becomes current again; when it was the only one the zorgtraject is left without a dossierverantwoordelijke.  This is the only way to undo an entry: appending a period cannot express \"this should never have been recorded\". The removal is written to the dossier audit log, so the mistake and its correction both stay visible (ADR-011). 
     * @summary Remove the current dossierverantwoordelijke period
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {string} [reden] Optional reason, recorded on the audit entry
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public verwijderPrimairBehandelaar(zorgtrajectId: string, reden?: string, options?: RawAxiosRequestConfig) {
        return PrimairBehandelaarApiFp(this.configuration).verwijderPrimairBehandelaar(zorgtrajectId, reden, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Changes the primair behandelaar (dossierverantwoordelijke) of the zorgtraject. Creates a new period; the end date of the current period is derived from the new period\'s startdatum - 1 day.  Business rules: - Startdatum of new period must be > startdatum of current period - Future dates are allowed (schedule changes in advance) - The new primair behandelaar must be an enabled, designated behandelaar of the   organization (422 otherwise), the same pool the create path offers - Someone not yet on the client\'s behandelteam is added to it, which requires   `clients.assign` (403 otherwise); choosing a current member needs nothing more 
     * @summary Change primair behandelaar (wijziging)
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {WijzigPrimairBehandelaarRequest} wijzigPrimairBehandelaarRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public wijzigPrimairBehandelaar(zorgtrajectId: string, wijzigPrimairBehandelaarRequest: WijzigPrimairBehandelaarRequest, options?: RawAxiosRequestConfig) {
        return PrimairBehandelaarApiFp(this.configuration).wijzigPrimairBehandelaar(zorgtrajectId, wijzigPrimairBehandelaarRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ReferenceDataApi - axios parameter creator
 */
export const ReferenceDataApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Returns the CL0005-NZA codes that are valid on `peildatum`, with their Betekenis exactly as Vektis publishes it. Used for dropdown population.  Retired codes are omitted: `08` (Dissociatieve stoornissen) and `20` (Overige psychische stoornissen) expired on 2022-01-02, so a present-day peildatum yields 14 codes. Passing a historical peildatum yields the list as it stood then. 
         * @summary List the DSM hoofdgroep codes valid on a date
         * @param {string} [peildatum] Date to evaluate validity on. Defaults to today (Europe/Amsterdam) when omitted. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listDsmHoofdgroepCodes: async (peildatum?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/reference-data/dsm-hoofdgroep-codes`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (peildatum !== undefined) {
                localVarQueryParameter['peildatum'] = (peildatum as any instanceof Date) ?
                    (peildatum as any).toISOString().substring(0,10) :
                    peildatum;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ReferenceDataApi - functional programming interface
 */
export const ReferenceDataApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ReferenceDataApiAxiosParamCreator(configuration)
    return {
        /**
         * Returns the CL0005-NZA codes that are valid on `peildatum`, with their Betekenis exactly as Vektis publishes it. Used for dropdown population.  Retired codes are omitted: `08` (Dissociatieve stoornissen) and `20` (Overige psychische stoornissen) expired on 2022-01-02, so a present-day peildatum yields 14 codes. Passing a historical peildatum yields the list as it stood then. 
         * @summary List the DSM hoofdgroep codes valid on a date
         * @param {string} [peildatum] Date to evaluate validity on. Defaults to today (Europe/Amsterdam) when omitted. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listDsmHoofdgroepCodes(peildatum?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DsmHoofdgroepCodeListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listDsmHoofdgroepCodes(peildatum, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReferenceDataApi.listDsmHoofdgroepCodes']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ReferenceDataApi - factory interface
 */
export const ReferenceDataApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ReferenceDataApiFp(configuration)
    return {
        /**
         * Returns the CL0005-NZA codes that are valid on `peildatum`, with their Betekenis exactly as Vektis publishes it. Used for dropdown population.  Retired codes are omitted: `08` (Dissociatieve stoornissen) and `20` (Overige psychische stoornissen) expired on 2022-01-02, so a present-day peildatum yields 14 codes. Passing a historical peildatum yields the list as it stood then. 
         * @summary List the DSM hoofdgroep codes valid on a date
         * @param {string} [peildatum] Date to evaluate validity on. Defaults to today (Europe/Amsterdam) when omitted. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listDsmHoofdgroepCodes(peildatum?: string, options?: RawAxiosRequestConfig): AxiosPromise<DsmHoofdgroepCodeListResponse> {
            return localVarFp.listDsmHoofdgroepCodes(peildatum, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ReferenceDataApi - object-oriented interface
 */
export class ReferenceDataApi extends BaseAPI {
    /**
     * Returns the CL0005-NZA codes that are valid on `peildatum`, with their Betekenis exactly as Vektis publishes it. Used for dropdown population.  Retired codes are omitted: `08` (Dissociatieve stoornissen) and `20` (Overige psychische stoornissen) expired on 2022-01-02, so a present-day peildatum yields 14 codes. Passing a historical peildatum yields the list as it stood then. 
     * @summary List the DSM hoofdgroep codes valid on a date
     * @param {string} [peildatum] Date to evaluate validity on. Defaults to today (Europe/Amsterdam) when omitted. 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listDsmHoofdgroepCodes(peildatum?: string, options?: RawAxiosRequestConfig) {
        return ReferenceDataApiFp(this.configuration).listDsmHoofdgroepCodes(peildatum, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * SettingApi - axios parameter creator
 */
export const SettingApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Corrects the running period in place. This is **not** `wijzigen`: appending a period says the behandelsetting changed as of a date, correcting says the entry itself was wrong. Conflating them leaves a mistake permanently in the clinical record (#3597).  Business rules: - The startdatum may move **backwards**, including before today. A value recorded on a   zorgtraject that already exists normally applies from the start of that traject, not   from the day the form happened to be opened. Since #4094 the setting is resolved per   prestatiedatum, so a startdatum a day late prices and declares every consult in the   gap under the wrong prestatiecode - A corrected startdatum that reaches onto or past the start of an earlier period   **replaces** it: that period is removed and this one takes over from the corrected   date. That is how \"it applied from the beginning\" is recorded once a wijziging has   already been appended (#4516). Reaching *into* an earlier period without covering its   start removes nothing — it simply ends the day before this one begins - The corrected startdatum may not fall before the zorgtraject\'s own startdatum (422) - Omitted properties keep their stored value  Only the current (latest) period is corrected; an earlier period is never edited, it is either left alone or removed whole by a correction that covers its start. Each removal is written to the dossier audit log naming the correction that replaced it (ADR-011). Billing lines already generated are **not** regenerated: a declared line is a record of what was sent. 
         * @summary Correct the current behandelsetting period
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {CorrigeerSettingRequest} corrigeerSettingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        corrigeerSetting: async (zorgtrajectId: string, corrigeerSettingRequest: CorrigeerSettingRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('corrigeerSetting', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'corrigeerSettingRequest' is not null or undefined
            assertParamExists('corrigeerSetting', 'corrigeerSettingRequest', corrigeerSettingRequest)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/setting/huidige`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(corrigeerSettingRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the current setting and complete history for a zorgtraject. History is ordered by startdatum descending (most recent first). 
         * @summary Get setting history for zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getSettingHistory: async (zorgtrajectId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('getSettingHistory', 'zorgtrajectId', zorgtrajectId)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/setting`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Removes the running period, which was recorded in error. The preceding period becomes current again; when it was the only one the zorgtraject is left without a behandelsetting, which `billing.missing-setting` signals.  This is the only way to undo an entry: appending a period cannot express \"this should never have been recorded\". The removal is written to the dossier audit log, so the mistake and its correction both stay visible (ADR-011). 
         * @summary Remove the current behandelsetting period
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} [reden] Optional reason, recorded on the audit entry
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        verwijderSetting: async (zorgtrajectId: string, reden?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('verwijderSetting', 'zorgtrajectId', zorgtrajectId)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/setting/huidige`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (reden !== undefined) {
                localVarQueryParameter['reden'] = reden;
            }

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Changes the setting of the zorgtraject. Creates a new setting period. The end date of the current period is derived from the new period\'s startdatum - 1 day.  Business rules: - Startdatum of new period must be > startdatum of current period - Future dates are allowed (schedule changes in advance) 
         * @summary Change setting (wijziging)
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {WijzigSettingRequest} wijzigSettingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wijzigSetting: async (zorgtrajectId: string, wijzigSettingRequest: WijzigSettingRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('wijzigSetting', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'wijzigSettingRequest' is not null or undefined
            assertParamExists('wijzigSetting', 'wijzigSettingRequest', wijzigSettingRequest)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/setting/wijzigen`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(wijzigSettingRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * SettingApi - functional programming interface
 */
export const SettingApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = SettingApiAxiosParamCreator(configuration)
    return {
        /**
         * Corrects the running period in place. This is **not** `wijzigen`: appending a period says the behandelsetting changed as of a date, correcting says the entry itself was wrong. Conflating them leaves a mistake permanently in the clinical record (#3597).  Business rules: - The startdatum may move **backwards**, including before today. A value recorded on a   zorgtraject that already exists normally applies from the start of that traject, not   from the day the form happened to be opened. Since #4094 the setting is resolved per   prestatiedatum, so a startdatum a day late prices and declares every consult in the   gap under the wrong prestatiecode - A corrected startdatum that reaches onto or past the start of an earlier period   **replaces** it: that period is removed and this one takes over from the corrected   date. That is how \"it applied from the beginning\" is recorded once a wijziging has   already been appended (#4516). Reaching *into* an earlier period without covering its   start removes nothing — it simply ends the day before this one begins - The corrected startdatum may not fall before the zorgtraject\'s own startdatum (422) - Omitted properties keep their stored value  Only the current (latest) period is corrected; an earlier period is never edited, it is either left alone or removed whole by a correction that covers its start. Each removal is written to the dossier audit log naming the correction that replaced it (ADR-011). Billing lines already generated are **not** regenerated: a declared line is a record of what was sent. 
         * @summary Correct the current behandelsetting period
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {CorrigeerSettingRequest} corrigeerSettingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async corrigeerSetting(zorgtrajectId: string, corrigeerSettingRequest: CorrigeerSettingRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SettingPeriodResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.corrigeerSetting(zorgtrajectId, corrigeerSettingRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SettingApi.corrigeerSetting']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the current setting and complete history for a zorgtraject. History is ordered by startdatum descending (most recent first). 
         * @summary Get setting history for zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getSettingHistory(zorgtrajectId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SettingHistoryResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getSettingHistory(zorgtrajectId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SettingApi.getSettingHistory']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Removes the running period, which was recorded in error. The preceding period becomes current again; when it was the only one the zorgtraject is left without a behandelsetting, which `billing.missing-setting` signals.  This is the only way to undo an entry: appending a period cannot express \"this should never have been recorded\". The removal is written to the dossier audit log, so the mistake and its correction both stay visible (ADR-011). 
         * @summary Remove the current behandelsetting period
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} [reden] Optional reason, recorded on the audit entry
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async verwijderSetting(zorgtrajectId: string, reden?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.verwijderSetting(zorgtrajectId, reden, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SettingApi.verwijderSetting']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Changes the setting of the zorgtraject. Creates a new setting period. The end date of the current period is derived from the new period\'s startdatum - 1 day.  Business rules: - Startdatum of new period must be > startdatum of current period - Future dates are allowed (schedule changes in advance) 
         * @summary Change setting (wijziging)
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {WijzigSettingRequest} wijzigSettingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wijzigSetting(zorgtrajectId: string, wijzigSettingRequest: WijzigSettingRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SettingPeriodResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.wijzigSetting(zorgtrajectId, wijzigSettingRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SettingApi.wijzigSetting']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * SettingApi - factory interface
 */
export const SettingApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = SettingApiFp(configuration)
    return {
        /**
         * Corrects the running period in place. This is **not** `wijzigen`: appending a period says the behandelsetting changed as of a date, correcting says the entry itself was wrong. Conflating them leaves a mistake permanently in the clinical record (#3597).  Business rules: - The startdatum may move **backwards**, including before today. A value recorded on a   zorgtraject that already exists normally applies from the start of that traject, not   from the day the form happened to be opened. Since #4094 the setting is resolved per   prestatiedatum, so a startdatum a day late prices and declares every consult in the   gap under the wrong prestatiecode - A corrected startdatum that reaches onto or past the start of an earlier period   **replaces** it: that period is removed and this one takes over from the corrected   date. That is how \"it applied from the beginning\" is recorded once a wijziging has   already been appended (#4516). Reaching *into* an earlier period without covering its   start removes nothing — it simply ends the day before this one begins - The corrected startdatum may not fall before the zorgtraject\'s own startdatum (422) - Omitted properties keep their stored value  Only the current (latest) period is corrected; an earlier period is never edited, it is either left alone or removed whole by a correction that covers its start. Each removal is written to the dossier audit log naming the correction that replaced it (ADR-011). Billing lines already generated are **not** regenerated: a declared line is a record of what was sent. 
         * @summary Correct the current behandelsetting period
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {CorrigeerSettingRequest} corrigeerSettingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        corrigeerSetting(zorgtrajectId: string, corrigeerSettingRequest: CorrigeerSettingRequest, options?: RawAxiosRequestConfig): AxiosPromise<SettingPeriodResponse> {
            return localVarFp.corrigeerSetting(zorgtrajectId, corrigeerSettingRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the current setting and complete history for a zorgtraject. History is ordered by startdatum descending (most recent first). 
         * @summary Get setting history for zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getSettingHistory(zorgtrajectId: string, options?: RawAxiosRequestConfig): AxiosPromise<SettingHistoryResponse> {
            return localVarFp.getSettingHistory(zorgtrajectId, options).then((request) => request(axios, basePath));
        },
        /**
         * Removes the running period, which was recorded in error. The preceding period becomes current again; when it was the only one the zorgtraject is left without a behandelsetting, which `billing.missing-setting` signals.  This is the only way to undo an entry: appending a period cannot express \"this should never have been recorded\". The removal is written to the dossier audit log, so the mistake and its correction both stay visible (ADR-011). 
         * @summary Remove the current behandelsetting period
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} [reden] Optional reason, recorded on the audit entry
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        verwijderSetting(zorgtrajectId: string, reden?: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.verwijderSetting(zorgtrajectId, reden, options).then((request) => request(axios, basePath));
        },
        /**
         * Changes the setting of the zorgtraject. Creates a new setting period. The end date of the current period is derived from the new period\'s startdatum - 1 day.  Business rules: - Startdatum of new period must be > startdatum of current period - Future dates are allowed (schedule changes in advance) 
         * @summary Change setting (wijziging)
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {WijzigSettingRequest} wijzigSettingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wijzigSetting(zorgtrajectId: string, wijzigSettingRequest: WijzigSettingRequest, options?: RawAxiosRequestConfig): AxiosPromise<SettingPeriodResponse> {
            return localVarFp.wijzigSetting(zorgtrajectId, wijzigSettingRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * SettingApi - object-oriented interface
 */
export class SettingApi extends BaseAPI {
    /**
     * Corrects the running period in place. This is **not** `wijzigen`: appending a period says the behandelsetting changed as of a date, correcting says the entry itself was wrong. Conflating them leaves a mistake permanently in the clinical record (#3597).  Business rules: - The startdatum may move **backwards**, including before today. A value recorded on a   zorgtraject that already exists normally applies from the start of that traject, not   from the day the form happened to be opened. Since #4094 the setting is resolved per   prestatiedatum, so a startdatum a day late prices and declares every consult in the   gap under the wrong prestatiecode - A corrected startdatum that reaches onto or past the start of an earlier period   **replaces** it: that period is removed and this one takes over from the corrected   date. That is how \"it applied from the beginning\" is recorded once a wijziging has   already been appended (#4516). Reaching *into* an earlier period without covering its   start removes nothing — it simply ends the day before this one begins - The corrected startdatum may not fall before the zorgtraject\'s own startdatum (422) - Omitted properties keep their stored value  Only the current (latest) period is corrected; an earlier period is never edited, it is either left alone or removed whole by a correction that covers its start. Each removal is written to the dossier audit log naming the correction that replaced it (ADR-011). Billing lines already generated are **not** regenerated: a declared line is a record of what was sent. 
     * @summary Correct the current behandelsetting period
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {CorrigeerSettingRequest} corrigeerSettingRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public corrigeerSetting(zorgtrajectId: string, corrigeerSettingRequest: CorrigeerSettingRequest, options?: RawAxiosRequestConfig) {
        return SettingApiFp(this.configuration).corrigeerSetting(zorgtrajectId, corrigeerSettingRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the current setting and complete history for a zorgtraject. History is ordered by startdatum descending (most recent first). 
     * @summary Get setting history for zorgtraject
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getSettingHistory(zorgtrajectId: string, options?: RawAxiosRequestConfig) {
        return SettingApiFp(this.configuration).getSettingHistory(zorgtrajectId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Removes the running period, which was recorded in error. The preceding period becomes current again; when it was the only one the zorgtraject is left without a behandelsetting, which `billing.missing-setting` signals.  This is the only way to undo an entry: appending a period cannot express \"this should never have been recorded\". The removal is written to the dossier audit log, so the mistake and its correction both stay visible (ADR-011). 
     * @summary Remove the current behandelsetting period
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {string} [reden] Optional reason, recorded on the audit entry
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public verwijderSetting(zorgtrajectId: string, reden?: string, options?: RawAxiosRequestConfig) {
        return SettingApiFp(this.configuration).verwijderSetting(zorgtrajectId, reden, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Changes the setting of the zorgtraject. Creates a new setting period. The end date of the current period is derived from the new period\'s startdatum - 1 day.  Business rules: - Startdatum of new period must be > startdatum of current period - Future dates are allowed (schedule changes in advance) 
     * @summary Change setting (wijziging)
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {WijzigSettingRequest} wijzigSettingRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public wijzigSetting(zorgtrajectId: string, wijzigSettingRequest: WijzigSettingRequest, options?: RawAxiosRequestConfig) {
        return SettingApiFp(this.configuration).wijzigSetting(zorgtrajectId, wijzigSettingRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



